// Master-P3 Final Sweep — Video Preview Widget.
//
// Renders a single ``artifact_type="video"`` campaign_artifacts row
// (as produced by the fal.ai hero-video pipeline in
// domains/creative_video). Deliberately kept mount-cheap so the
// Campaign Preview page can render several video artifacts alongside
// image / lp_draft artifacts without regressing initial-paint.
//
// Trust framework parity (Block-A):
// - Status pill  →  ready / generating / failed / superseded
// - Fallback pill → only shown when structured_content.is_fallback=true
// - Cost chip     → reuses the stub-vs-real convention from ModeChip
// - Skeleton       → shown while asset_url is missing but status !== failed
// - Empty state    → parent renders this if the artifact is null
import React from "react";

function statusColor(status) {
  if (status === "ready") return { bg: "#0e4429", label: "Ready" };
  if (status === "generating" || status === "queued")
    return { bg: "#5a4a10", label: status };
  if (status === "failed") return { bg: "#8b1a1a", label: "Failed" };
  if (status === "superseded")
    return { bg: "#8b1a1a", label: "SUPERSEDED" };
  return { bg: "#3a3a3a", label: status || "unknown" };
}

export function VideoPreviewSkeleton() {
  return (
    <div data-testid="video-preview-skeleton"
      style={{ border: "1px solid #333", borderRadius: 8, padding: 12,
        marginBottom: 10, background: "#0a0a0a",
        minHeight: 220, display: "flex", flexDirection: "column",
        alignItems: "stretch", justifyContent: "center" }}>
      <div style={{ height: 200, background: "#1a1a1a",
        borderRadius: 6, marginBottom: 8,
        animation: "pulse 1.5s ease-in-out infinite" }} />
      <div style={{ height: 12, background: "#1a1a1a",
        borderRadius: 4, width: "60%" }} />
    </div>
  );
}

export function VideoPreviewEmpty() {
  return (
    <p data-testid="empty-video" style={{ color: "#888" }}>
      Noch kein Video generiert.
    </p>
  );
}

/**
 * @param {Object} artifact — campaign_artifacts row of type "video".
 *   structured_content shape produced by the hero-video pipeline:
 *   { asset_url, asset_key, duration_seconds, aspect_ratio,
 *     provider, model, is_fallback, prompt }
 */
export default function VideoPreviewWidget({ artifact }) {
  if (!artifact) return <VideoPreviewEmpty />;
  const s = artifact.structured_content || {};
  const isFallback = !!s.is_fallback;
  const status = artifact.status || "ready";
  const isReady = status === "ready" && !!s.asset_url;
  const { bg: statusBg, label: statusLabel } = statusColor(status);
  const isReal = artifact.execution_mode &&
                    artifact.execution_mode.startsWith("real");

  return (
    <div
      data-testid={`video-preview-${artifact.id}`}
      style={{ border: "1px solid #333", borderRadius: 8, padding: 12,
        marginBottom: 10,
        opacity: status === "superseded" ? 0.5 : 1 }}>
      <div style={{ marginBottom: 8, display: "flex",
        flexWrap: "wrap", alignItems: "center", gap: 6 }}>
        <span data-testid={`video-status-${status}`}
          style={{ padding: "2px 8px", borderRadius: 12,
            background: statusBg, color: "#fff", fontSize: 12 }}>
          {statusLabel}
        </span>
        <span data-testid={`video-mode-${isReal ? "real" : "stub"}`}
          style={{ padding: "2px 8px", borderRadius: 12,
            background: isReal ? "#0e4429" : "#3a3a3a",
            color: "#fff", fontSize: 12 }}>
          {isReal ? `Model: Real (${artifact.execution_mode})`
                   : "Model: Stub"}
        </span>
        {isFallback && (
          <span data-testid="video-fallback-badge"
            style={{ padding: "2px 8px", borderRadius: 12,
              background: "#8b1a1a", color: "#fff", fontSize: 12,
              fontWeight: 600 }}>
            FALLBACK / SIMULATION
          </span>
        )}
        {s.aspect_ratio && (
          <span data-testid="video-aspect"
            style={{ padding: "2px 8px", borderRadius: 12,
              background: "#1a3a5c", color: "#fff", fontSize: 12 }}>
            {s.aspect_ratio}
          </span>
        )}
        {s.duration_seconds != null && (
          <span data-testid="video-duration"
            style={{ padding: "2px 8px", borderRadius: 12,
              background: "#1a3a5c", color: "#fff", fontSize: 12 }}>
            {Number(s.duration_seconds).toFixed(1)}s
          </span>
        )}
        {artifact.brief_version != null && (
          <span data-testid="video-version"
            style={{ padding: "2px 8px", borderRadius: 12,
              background: "#1a3a5c", color: "#fff", fontSize: 12 }}>
            Brief v{artifact.brief_version}
          </span>
        )}
      </div>

      {isReady ? (
        <video
          data-testid={`video-player-${artifact.id}`}
          controls
          preload="metadata"
          src={s.asset_url}
          poster={s.poster_url || undefined}
          style={{ width: "100%", maxHeight: 360,
            background: "#000", borderRadius: 6 }}>
          Your browser does not support HTML5 video.
        </video>
      ) : status === "failed" ? (
        <div data-testid={`video-error-${artifact.id}`}
          style={{ background: "#3a1010", color: "#f0b0b0",
            borderRadius: 6, padding: 12, fontSize: 13 }}>
          {s.error || artifact.note ||
             "Video generation failed. Try regenerating."}
        </div>
      ) : (
        <VideoPreviewSkeleton />
      )}

      {(s.provider || s.model) && (
        <div data-testid={`video-provenance-${artifact.id}`}
          style={{ marginTop: 8, fontSize: 11, color: "#888" }}>
          {s.provider ? `provider: ${s.provider}` : ""}
          {s.provider && s.model ? " · " : ""}
          {s.model ? `model: ${s.model}` : ""}
        </div>
      )}
    </div>
  );
}
